
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { getMyTournaments, startTournament, cancelTournament, updateTournamentStatus } from '../../services/supabaseService';
import { motion, AnimatePresence } from 'motion/react';

export const ManageTournaments: React.FC = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const [tournaments, setTournaments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [tournamentToCancel, setTournamentToCancel] = useState<any | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [tournamentForStatusChange, setTournamentForStatusChange] = useState<any | null>(null);
    const [statusConfirmTarget, setStatusConfirmTarget] = useState<string | null>(null);
    const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);

    const isTerminalStatus = (status: string) => {
        const s = (status || '').toLowerCase();
        return s === 'finished' || s === 'cancelled' || s === 'canceled';
    };

    const handleSelectStatus = (tournament: any, newStatus: string) => {
        if (!tournament) return;
        if (isTerminalStatus(tournament.status)) {
            showNotification("Este torneio já foi finalizado ou cancelado e seu status não pode mais ser alterado.", "error");
            return;
        }

        const currentNorm = (tournament.status || '').toLowerCase();
        const targetNorm = newStatus.toLowerCase();
        if (currentNorm === targetNorm || (targetNorm === 'in_progress' && (currentNorm === 'in_progress' || currentNorm === 'running'))) {
            return;
        }

        // Se for status definitivo, pede confirmação explícita
        if (targetNorm === 'finished' || targetNorm === 'cancelled' || targetNorm === 'canceled') {
            setStatusConfirmTarget(targetNorm === 'canceled' ? 'cancelled' : targetNorm);
            return;
        }

        executeStatusChange(tournament, targetNorm);
    };

    const executeStatusChange = async (tournament: any, newStatus: string) => {
        if (!tournament) return;
        setUpdatingStatus(true);
        try {
            const res = await updateTournamentStatus(tournament.id, newStatus);
            if (res && 'success' in res && res.success) {
                const label = getStatusLabel(newStatus);
                const isDefinitive = newStatus === 'finished' || newStatus === 'cancelled' || newStatus === 'canceled';
                showNotification(
                    isDefinitive 
                        ? `Torneio ${label.toLowerCase()} com sucesso! O status foi consolidado em definitivo.`
                        : `Status do torneio alterado para "${label}"!`, 
                    "success"
                );
                setTournamentForStatusChange(null);
                setStatusConfirmTarget(null);
                fetchTournaments();
            } else {
                showNotification((res as any)?.error || "Erro ao atualizar status do torneio", "error");
            }
        } catch (err: any) {
            showNotification(err?.message || "Erro ao atualizar status", "error");
        } finally {
            setUpdatingStatus(false);
        }
    };

    useEffect(() => {
        fetchTournaments();
    }, []);

    const fetchTournaments = async () => {
        setLoading(true);
        try {
            const data = await getMyTournaments();
            setTournaments(data);
        } catch (error) {
            showNotification("Erro ao carregar torneios", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleStartTournament = async (id: number) => {
        try {
            const res = await startTournament(id);
            if (res && 'success' in res && res.success) {
                showNotification("Torneio iniciado com sucesso!", "success");
                fetchTournaments();
            } else {
                showNotification((res as any)?.error || "Erro ao iniciar torneio", "error");
            }
        } catch (error) {
            showNotification("Erro ao iniciar torneio", "error");
        }
    };

    const handleConfirmCancel = async () => {
        if (!tournamentToCancel) return;
        setCancelling(true);
        try {
            const res = await cancelTournament(tournamentToCancel.id);
            if (res && 'success' in res && res.success) {
                showNotification("Torneio cancelado com sucesso sem declaração de vencedores!", "success");
                setTournamentToCancel(null);
                fetchTournaments();
            } else {
                showNotification((res as any)?.error || "Erro ao cancelar torneio", "error");
            }
        } catch (error) {
            showNotification("Erro ao cancelar torneio", "error");
        } finally {
            setCancelling(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'open': return 'text-emerald-400 bg-emerald-400/10';
            case 'in_progress':
            case 'running': return 'text-blue-400 bg-blue-400/10';
            case 'finished': return 'text-slate-400 bg-slate-400/10';
            case 'scheduled': return 'text-amber-400 bg-amber-400/10';
            case 'cancelled':
            case 'canceled': return 'text-rose-400 bg-rose-400/10';
            default: return 'text-slate-500 bg-slate-500/10';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'open': return 'Aberto';
            case 'in_progress':
            case 'running': return 'Em Andamento';
            case 'finished': return 'Finalizado';
            case 'scheduled': return 'Agendado';
            case 'cancelled':
            case 'canceled': return 'Cancelado';
            default: return status || 'Indefinido';
        }
    };

    const statusCounts = useMemo(() => {
        const counts = {
            all: tournaments.length,
            scheduled: 0,
            open: 0,
            in_progress: 0,
            finished: 0,
            cancelled: 0
        };
        tournaments.forEach(t => {
            const s = t.status?.toLowerCase();
            if (s === 'scheduled') counts.scheduled++;
            else if (s === 'open') counts.open++;
            else if (s === 'in_progress' || s === 'running') counts.in_progress++;
            else if (s === 'finished') counts.finished++;
            else if (s === 'cancelled' || s === 'canceled') counts.cancelled++;
        });
        return counts;
    }, [tournaments]);

    const sortedAndFilteredTournaments = useMemo(() => {
        let result = [...tournaments];

        // Status filter
        if (statusFilter !== 'all') {
            result = result.filter(t => {
                const s = t.status?.toLowerCase();
                if (statusFilter === 'in_progress') {
                    return s === 'in_progress' || s === 'running';
                }
                if (statusFilter === 'cancelled') {
                    return s === 'cancelled' || s === 'canceled';
                }
                return s === statusFilter;
            });
        }

        // Search filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(t => {
                const name = (t.name || '').toLowerCase();
                const game = (t.games?.name || t.cardgames?.name || '').toLowerCase();
                const format = (t.game_formats?.name || t.tournament_formats?.name || '').toLowerCase();
                const ticket = (t.ticket || '').toLowerCase();
                return name.includes(q) || game.includes(q) || format.includes(q) || ticket.includes(q);
            });
        }

        // Always sort by most recent date first (descending)
        result.sort((a, b) => {
            const dateA = new Date(a.starts_at || a.start_date || a.created_at || 0).getTime();
            const dateB = new Date(b.starts_at || b.start_date || b.created_at || 0).getTime();
            return dateB - dateA;
        });

        return result;
    }, [tournaments, statusFilter, searchQuery]);

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
    );

    return (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-white italic tracking-tighter">MEUS TORNEIOS</h1>
                    <p className="text-slate-400 font-medium">Gerencie suas competições e eventos</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-3">
                    <Link 
                        to="/meus-torneios/importar"
                        className="bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-700/80 font-bold px-5 py-3 rounded-2xl transition-colors flex items-center justify-center space-x-2 text-xs"
                    >
                        <i className="fas fa-file-import text-purple-400"></i>
                        <span>IMPORTAR RESULTADOS</span>
                    </Link>

                    <Link 
                        to="/novo-torneio"
                        className="bg-purple-600 hover:bg-purple-500 text-white font-black px-6 py-3 rounded-2xl transition-colors flex items-center justify-center space-x-2 text-xs"
                    >
                        <i className="fas fa-plus"></i>
                        <span>CRIAR TORNEIO</span>
                    </Link>
                </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-2xl space-y-3.5">
                <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                    {/* Search Input */}
                    <div className="relative flex-1">
                        <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Buscar torneio por nome, modalidade ou ticket..."
                            className="w-full bg-slate-950/70 border border-slate-800 focus:border-purple-500/60 text-slate-200 placeholder:text-slate-500 pl-9 pr-8 py-2.5 rounded-xl text-xs font-medium outline-none transition-colors"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs cursor-pointer"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                    </div>

                    {/* Sorting indicator */}
                    <div className="flex items-center gap-2 shrink-0 text-slate-400 text-xs font-bold px-3 py-2 bg-slate-950/40 border border-slate-800 rounded-xl select-none">
                        <i className="fas fa-arrow-down-wide-short text-purple-400"></i>
                        <span className="text-[11px] text-slate-300">Mais recentes primeiro</span>
                    </div>
                </div>

                {/* Status Filter Pills */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-0.5 text-xs">
                    <button
                        type="button"
                        onClick={() => setStatusFilter('all')}
                        className={`px-3.5 py-1.5 rounded-xl font-bold uppercase tracking-wider text-[11px] transition-all flex items-center space-x-1.5 shrink-0 cursor-pointer ${
                            statusFilter === 'all'
                                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                                : 'bg-slate-950/60 text-slate-400 hover:text-white border border-slate-800 hover:border-slate-700'
                        }`}
                    >
                        <span>Todos</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                            statusFilter === 'all' ? 'bg-purple-800/80 text-white' : 'bg-slate-800 text-slate-400'
                        }`}>
                            {statusCounts.all}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('scheduled')}
                        className={`px-3.5 py-1.5 rounded-xl font-bold uppercase tracking-wider text-[11px] transition-all flex items-center space-x-1.5 shrink-0 cursor-pointer ${
                            statusFilter === 'scheduled'
                                ? 'bg-amber-600 text-white shadow-md shadow-amber-600/20'
                                : 'bg-slate-950/60 text-amber-400 hover:text-amber-300 border border-slate-800 hover:border-amber-500/30'
                        }`}
                    >
                        <i className="fas fa-calendar-day text-[10px]"></i>
                        <span>Agendados</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                            statusFilter === 'scheduled' ? 'bg-amber-800/80 text-white' : 'bg-amber-950/60 text-amber-400 border border-amber-500/20'
                        }`}>
                            {statusCounts.scheduled}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('open')}
                        className={`px-3.5 py-1.5 rounded-xl font-bold uppercase tracking-wider text-[11px] transition-all flex items-center space-x-1.5 shrink-0 cursor-pointer ${
                            statusFilter === 'open'
                                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
                                : 'bg-slate-950/60 text-emerald-400 hover:text-emerald-300 border border-slate-800 hover:border-emerald-500/30'
                        }`}
                    >
                        <i className="fas fa-door-open text-[10px]"></i>
                        <span>Abertos</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                            statusFilter === 'open' ? 'bg-emerald-800/80 text-white' : 'bg-emerald-950/60 text-emerald-400 border border-emerald-500/20'
                        }`}>
                            {statusCounts.open}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('in_progress')}
                        className={`px-3.5 py-1.5 rounded-xl font-bold uppercase tracking-wider text-[11px] transition-all flex items-center space-x-1.5 shrink-0 cursor-pointer ${
                            statusFilter === 'in_progress'
                                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                                : 'bg-slate-950/60 text-blue-400 hover:text-blue-300 border border-slate-800 hover:border-blue-500/30'
                        }`}
                    >
                        <i className="fas fa-play text-[9px]"></i>
                        <span>Em Andamento</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                            statusFilter === 'in_progress' ? 'bg-blue-800/80 text-white' : 'bg-blue-950/60 text-blue-400 border border-blue-500/20'
                        }`}>
                            {statusCounts.in_progress}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('finished')}
                        className={`px-3.5 py-1.5 rounded-xl font-bold uppercase tracking-wider text-[11px] transition-all flex items-center space-x-1.5 shrink-0 cursor-pointer ${
                            statusFilter === 'finished'
                                ? 'bg-slate-700 text-white shadow-md shadow-slate-700/20'
                                : 'bg-slate-950/60 text-slate-400 hover:text-slate-200 border border-slate-800 hover:border-slate-700'
                        }`}
                    >
                        <i className="fas fa-check-circle text-[10px]"></i>
                        <span>Finalizados</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                            statusFilter === 'finished' ? 'bg-slate-800 text-white' : 'bg-slate-800 text-slate-400'
                        }`}>
                            {statusCounts.finished}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter('cancelled')}
                        className={`px-3.5 py-1.5 rounded-xl font-bold uppercase tracking-wider text-[11px] transition-all flex items-center space-x-1.5 shrink-0 cursor-pointer ${
                            statusFilter === 'cancelled'
                                ? 'bg-rose-600 text-white shadow-md shadow-rose-600/20'
                                : 'bg-slate-950/60 text-rose-400 hover:text-rose-300 border border-slate-800 hover:border-rose-500/30'
                        }`}
                    >
                        <i className="fas fa-ban text-[10px]"></i>
                        <span>Cancelados</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                            statusFilter === 'cancelled' ? 'bg-rose-800/80 text-white' : 'bg-rose-950/60 text-rose-400 border border-rose-500/20'
                        }`}>
                            {statusCounts.cancelled}
                        </span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {tournaments.length === 0 ? (
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-12 text-center">
                        <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-500 text-2xl">
                            <i className="fas fa-trophy"></i>
                        </div>
                        <h3 className="text-white font-bold mb-2">Nenhum torneio cadastrado</h3>
                        <p className="text-slate-500 text-sm">Crie seu primeiro torneio para começar a organizar competições.</p>
                    </div>
                ) : sortedAndFilteredTournaments.length === 0 ? (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-12 text-center space-y-3">
                        <div className="w-14 h-14 bg-slate-800/80 border border-slate-700/60 rounded-full flex items-center justify-center mx-auto text-slate-500 text-xl">
                            <i className="fas fa-filter-circle-xmark"></i>
                        </div>
                        <h3 className="text-white font-bold text-base">Nenhum torneio encontrado</h3>
                        <p className="text-slate-500 text-xs max-w-sm mx-auto">
                            Nenhum torneio corresponde ao status ou termo de busca selecionado.
                        </p>
                        <button
                            type="button"
                            onClick={() => { setStatusFilter('all'); setSearchQuery(''); }}
                            className="text-xs text-purple-400 hover:text-purple-300 font-bold underline cursor-pointer"
                        >
                            Limpar filtros
                        </button>
                    </div>
                ) : (
                    sortedAndFilteredTournaments.map((t) => (
                        <div key={t.id} className="bg-slate-900 border border-slate-800 p-6 rounded-2xl flex flex-col md:flex-row items-center gap-6 group hover:border-slate-700 transition-colors">
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                    <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full ${getStatusColor(t.status)}`}>
                                        {getStatusLabel(t.status)}
                                    </span>
                                    {t.online ? (
                                        <span className="text-[10px] font-black uppercase tracking-widest text-cyan-400 bg-cyan-950/60 border border-cyan-800/40 px-2 py-0.5 rounded-full flex items-center space-x-1">
                                            <i className="fas fa-globe text-[9px]"></i>
                                            <span>Online</span>
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-black uppercase tracking-widest text-purple-400 bg-purple-950/60 border border-purple-800/40 px-2 py-0.5 rounded-full flex items-center space-x-1">
                                            <i className="fas fa-store text-[9px]"></i>
                                            <span>Presencial</span>
                                        </span>
                                    )}
                                    {t.decklist_required && (
                                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 bg-amber-950/60 border border-amber-800/40 px-2 py-0.5 rounded-full flex items-center space-x-1">
                                            <i className="fas fa-layer-group text-[9px]"></i>
                                            <span>Decklist</span>
                                        </span>
                                    )}
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-300 bg-slate-800 px-2 py-0.5 rounded-full">
                                        {t.games?.name || t.cardgames?.name || 'Geral'}
                                    </span>
                                    {(t.tournament_event_categories?.name || t.event_categories?.name) && (
                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-800/80 px-2 py-0.5 rounded-full">
                                            {t.tournament_event_categories?.name || t.event_categories?.name}
                                        </span>
                                    )}
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-800/60 px-2 py-0.5 rounded-full">
                                        {t.game_formats?.name || t.tournament_formats?.name || 'Livre'}
                                    </span>
                                    {t.game_sets?.code && (
                                        <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-800/40 px-2 py-0.5 rounded-full">
                                            {t.game_sets.code}
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-1.5">{t.name}</h3>
                                <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-slate-400 text-xs">
                                    <span className="flex items-center space-x-1">
                                        <i className="far fa-calendar"></i>
                                        <span>{t.starts_at || t.start_date ? new Date(t.starts_at || t.start_date).toLocaleDateString('pt-BR') : 'Data a definir'}</span>
                                    </span>
                                    <span className="flex items-center space-x-1">
                                        <i className="far fa-clock"></i>
                                        <span>{t.starts_at || t.start_date ? new Date(t.starts_at || t.start_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                    </span>
                                    <span className="flex items-center space-x-1">
                                        <i className="fas fa-users"></i>
                                        <span>Máx: {t.max_players || 8}</span>
                                    </span>
                                    {t.ticket && (
                                        <span className="flex items-center space-x-1 text-purple-400 font-mono font-bold">
                                            <i className="fas fa-ticket"></i>
                                            <span>{t.ticket}</span>
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center space-x-2 w-full md:w-auto">
                                <Link 
                                    to={`/torneio/${t.id}`}
                                    className="flex-1 md:flex-none bg-slate-800 hover:bg-slate-700 text-white text-xs font-black px-4 py-3 rounded-xl transition-all uppercase tracking-widest text-center"
                                >
                                    Ver
                                </Link>
                                <Link 
                                    to={`/torneio/${t.id}/gerenciar`}
                                    className="flex-1 md:flex-none border border-slate-700 hover:border-purple-500 hover:text-purple-400 text-slate-400 text-xs font-black px-4 py-3 rounded-xl transition-all uppercase tracking-widest text-center"
                                >
                                    Gerenciar
                                </Link>
                                <Link 
                                    to={`/meus-torneios/importar?tournamentId=${t.id}`}
                                    className="flex-1 md:flex-none border border-slate-700 hover:border-amber-500 hover:text-amber-400 text-slate-400 text-xs font-black px-4 py-3 rounded-xl transition-all uppercase tracking-widest text-center"
                                >
                                    Classificação
                                </Link>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal: Confirm Tournament Cancellation */}
            {tournamentToCancel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 animate-in fade-in">
                    <div className="bg-slate-900 border border-rose-800/60 w-full max-w-md rounded-2xl p-6 sm:p-8 space-y-6 relative">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-rose-400 text-xl shrink-0">
                                <i className="fas fa-triangle-exclamation"></i>
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-white italic tracking-tight uppercase">Cancelar Torneio</h3>
                                <p className="text-xs text-rose-400 font-bold">Sem declaração de vencedores</p>
                            </div>
                        </div>

                        <div className="space-y-3 text-xs text-slate-300 leading-relaxed">
                            <p>
                                Tem certeza de que deseja cancelar o torneio <strong className="text-white">"{tournamentToCancel.name}"</strong>?
                            </p>
                            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3.5 text-slate-400 space-y-1.5 text-[11px]">
                                <div className="flex items-center space-x-1.5 text-amber-400 font-bold">
                                    <i className="fas fa-info-circle"></i>
                                    <span>Ao cancelar este torneio:</span>
                                </div>
                                <ul className="list-disc list-inside space-y-1.5 pl-1">
                                    <li>O status passará a ser <strong className="text-rose-300">CANCELADO</strong>.</li>
                                    <li><strong className="text-white">Nenhum vencedor</strong> precisará ser informado.</li>
                                    <li>As inscrições serão bloqueadas.</li>
                                    <li className="text-amber-300 font-bold">Ao cancelar, esta ação é definitiva e você NÃO poderá mais alterar o status deste torneio posteriormente.</li>
                                </ul>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 pt-2">
                            <button
                                type="button"
                                disabled={cancelling}
                                onClick={() => setTournamentToCancel(null)}
                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs py-3 rounded-xl transition-all uppercase tracking-wider cursor-pointer"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                disabled={cancelling}
                                onClick={handleConfirmCancel}
                                className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-black text-xs py-3 rounded-xl transition-all shadow-lg shadow-rose-600/20 uppercase tracking-wider flex items-center justify-center space-x-2 cursor-pointer"
                            >
                                {cancelling ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                        <span>Cancelando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-ban"></i>
                                        <span>Confirmar e Cancelar</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Change Tournament Status from List */}
            {tournamentForStatusChange && !statusConfirmTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-slate-900 border border-slate-800 w-full max-w-lg rounded-3xl p-6 md:p-8 space-y-5 relative shadow-2xl">
                        <button
                            onClick={() => setTournamentForStatusChange(null)}
                            className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors cursor-pointer"
                        >
                            <i className="fas fa-times text-lg"></i>
                        </button>

                        <div className="space-y-1">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-black text-cyan-300 uppercase tracking-widest">
                                <i className="fas fa-arrows-rotate"></i> Gerenciar Status
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                                Alterar Status do Torneio
                            </h3>
                            <p className="text-xs text-slate-400">
                                Torneio: <strong className="text-white">{tournamentForStatusChange.name}</strong>
                            </p>
                        </div>

                        <div className="space-y-2.5">
                            {/* Option 1: Agendado */}
                            <button
                                type="button"
                                disabled={updatingStatus || tournamentForStatusChange.status === 'scheduled'}
                                onClick={() => handleSelectStatus(tournamentForStatusChange, 'scheduled')}
                                className={`w-full p-3.5 rounded-2xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                                    tournamentForStatusChange.status === 'scheduled'
                                        ? 'bg-amber-500/15 border-amber-500 text-white ring-1 ring-amber-500'
                                        : 'bg-slate-950/70 border-slate-800 text-slate-300 hover:border-amber-500/50 hover:bg-slate-950'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center justify-center text-sm shrink-0">
                                        <i className="fas fa-calendar-day"></i>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-black uppercase text-amber-300">Agendado</span>
                                            {tournamentForStatusChange.status === 'scheduled' && <span className="text-[9px] bg-amber-500/30 text-amber-200 px-1.5 py-0.5 rounded font-black">Atual</span>}
                                        </div>
                                        <p className="text-[11px] text-slate-400">Torneio programado para acontecer em data futura.</p>
                                    </div>
                                </div>
                                <i className="fas fa-chevron-right text-xs text-slate-500"></i>
                            </button>

                            {/* Option 2: Aberto */}
                            <button
                                type="button"
                                disabled={updatingStatus || tournamentForStatusChange.status === 'open'}
                                onClick={() => handleSelectStatus(tournamentForStatusChange, 'open')}
                                className={`w-full p-3.5 rounded-2xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                                    tournamentForStatusChange.status === 'open'
                                        ? 'bg-emerald-500/15 border-emerald-500 text-white ring-1 ring-emerald-500'
                                        : 'bg-slate-950/70 border-slate-800 text-slate-300 hover:border-emerald-500/50 hover:bg-slate-950'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center text-sm shrink-0">
                                        <i className="fas fa-door-open"></i>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-black uppercase text-emerald-300">Aberto</span>
                                            {tournamentForStatusChange.status === 'open' && <span className="text-[9px] bg-emerald-500/30 text-emerald-200 px-1.5 py-0.5 rounded font-black">Atual</span>}
                                        </div>
                                        <p className="text-[11px] text-slate-400">Inscrições e check-in disponíveis para jogadores.</p>
                                    </div>
                                </div>
                                <i className="fas fa-chevron-right text-xs text-slate-500"></i>
                            </button>

                            {/* Option 3: Em Andamento */}
                            <button
                                type="button"
                                disabled={updatingStatus || ['in_progress', 'running'].includes(tournamentForStatusChange.status)}
                                onClick={() => handleSelectStatus(tournamentForStatusChange, 'in_progress')}
                                className={`w-full p-3.5 rounded-2xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                                    ['in_progress', 'running'].includes(tournamentForStatusChange.status)
                                        ? 'bg-blue-500/15 border-blue-500 text-white ring-1 ring-blue-500'
                                        : 'bg-slate-950/70 border-slate-800 text-slate-300 hover:border-blue-500/50 hover:bg-slate-950'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center justify-center text-sm shrink-0">
                                        <i className="fas fa-play"></i>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-black uppercase text-blue-300">Em Andamento</span>
                                            {['in_progress', 'running'].includes(tournamentForStatusChange.status) && <span className="text-[9px] bg-blue-500/30 text-blue-200 px-1.5 py-0.5 rounded font-black">Atual</span>}
                                        </div>
                                        <p className="text-[11px] text-slate-400">Partidas e rodadas em disputa.</p>
                                    </div>
                                </div>
                                <i className="fas fa-chevron-right text-xs text-slate-500"></i>
                            </button>

                            {/* Option 4: Finalizado (Requer confirmação de irreversibilidade) */}
                            <button
                                type="button"
                                disabled={updatingStatus}
                                onClick={() => setStatusConfirmTarget('finished')}
                                className="w-full p-3.5 rounded-2xl border border-slate-800 bg-slate-950/70 text-slate-300 hover:border-purple-500/50 hover:bg-slate-950 text-left transition-all flex items-center justify-between cursor-pointer"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center text-sm shrink-0">
                                        <i className="fas fa-trophy"></i>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-black uppercase text-purple-300">Finalizado</span>
                                            <span className="text-[9px] bg-purple-950 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded font-bold">Definitivo</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400">Encerra o torneio (não poderá mais mudar o status).</p>
                                    </div>
                                </div>
                                <i className="fas fa-lock text-xs text-purple-400"></i>
                            </button>

                            {/* Option 5: Cancelado (Requer confirmação de irreversibilidade) */}
                            <button
                                type="button"
                                disabled={updatingStatus}
                                onClick={() => setStatusConfirmTarget('cancelled')}
                                className="w-full p-3.5 rounded-2xl border border-slate-800 bg-slate-950/70 text-slate-300 hover:border-rose-500/50 hover:bg-slate-950 text-left transition-all flex items-center justify-between cursor-pointer"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center text-sm shrink-0">
                                        <i className="fas fa-ban"></i>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-black uppercase text-rose-300">Cancelado</span>
                                            <span className="text-[9px] bg-rose-950 text-rose-300 border border-rose-500/30 px-1.5 py-0.5 rounded font-bold">Definitivo</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400">Cancela o torneio sem vencedores (não poderá mais mudar o status).</p>
                                    </div>
                                </div>
                                <i className="fas fa-lock text-xs text-rose-400"></i>
                            </button>
                        </div>

                        <div className="p-3 bg-slate-950/80 rounded-2xl border border-slate-800 text-[11px] text-slate-400 flex items-start gap-2">
                            <i className="fas fa-info-circle text-amber-400 mt-0.5 shrink-0"></i>
                            <span>
                                Você pode alternar livremente entre <strong>Agendado</strong>, <strong>Aberto</strong> e <strong>Em Andamento</strong>. Ao escolher <strong>Finalizado</strong> ou <strong>Cancelado</strong>, o status será permanentemente bloqueado.
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Confirmation for Terminal Status (Finished or Cancelled) */}
            {tournamentForStatusChange && statusConfirmTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                    <div className={`bg-slate-900 border w-full max-w-md rounded-3xl p-6 sm:p-8 space-y-6 relative shadow-2xl ${
                        statusConfirmTarget === 'cancelled' ? 'border-rose-800/80' : 'border-purple-600/80'
                    }`}>
                        <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shrink-0 border ${
                                statusConfirmTarget === 'cancelled' 
                                    ? 'bg-rose-500/20 border-rose-500/40 text-rose-400' 
                                    : 'bg-purple-500/20 border-purple-500/40 text-purple-400'
                            }`}>
                                <i className="fas fa-triangle-exclamation"></i>
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-white italic tracking-tight uppercase">
                                    {statusConfirmTarget === 'cancelled' ? 'Cancelar Torneio' : 'Finalizar Torneio'}
                                </h3>
                                <p className={`text-xs font-bold ${
                                    statusConfirmTarget === 'cancelled' ? 'text-rose-400' : 'text-purple-400'
                                }`}>
                                    Status Definitivo e Irreversível
                                </p>
                            </div>
                        </div>

                        <div className="space-y-3 text-xs text-slate-300 leading-relaxed">
                            <p>
                                Deseja realmente alterar o status do torneio <strong className="text-white">"{tournamentForStatusChange.name}"</strong> para <strong className={statusConfirmTarget === 'cancelled' ? 'text-rose-400' : 'text-purple-300'}>{statusConfirmTarget === 'cancelled' ? 'CANCELADO' : 'FINALIZADO'}</strong>?
                            </p>
                            <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3.5 text-slate-300 space-y-2 text-[11px]">
                                <div className="flex items-center space-x-1.5 text-amber-400 font-bold">
                                    <i className="fas fa-lock"></i>
                                    <span>Confirmação Obrigatória:</span>
                                </div>
                                <p className="text-slate-300 leading-normal">
                                    Ao {statusConfirmTarget === 'cancelled' ? 'cancelar' : 'finalizar'} este torneio, <strong className="text-white">você NÃO poderá mais mudar o status deste torneio posteriormente</strong>. O torneio ficará permanentemente congelado nesse estado.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 pt-2">
                            <button
                                type="button"
                                disabled={updatingStatus}
                                onClick={() => setStatusConfirmTarget(null)}
                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs py-3 rounded-xl transition-all uppercase tracking-wider cursor-pointer"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                disabled={updatingStatus}
                                onClick={() => executeStatusChange(tournamentForStatusChange, statusConfirmTarget)}
                                className={`flex-1 disabled:opacity-50 text-white font-black text-xs py-3 rounded-xl transition-all shadow-lg uppercase tracking-wider flex items-center justify-center space-x-2 cursor-pointer ${
                                    statusConfirmTarget === 'cancelled'
                                        ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/20'
                                        : 'bg-purple-600 hover:bg-purple-500 shadow-purple-600/20'
                                }`}
                            >
                                {updatingStatus ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                        <span>Processando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className={statusConfirmTarget === 'cancelled' ? 'fas fa-ban' : 'fas fa-trophy'}></i>
                                        <span>{statusConfirmTarget === 'cancelled' ? 'Sim, Cancelar' : 'Sim, Finalizar'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
